import {
    updatePrioritizeRequestStatusMutator,
    PrioritizeRequestStatus,
    getUserStore,
} from 'owa-mail-priority-inbox-store';
import { ArrowCircleUpSparkleRegular } from '@fluentui/react-icons';
import loc, { formatToArray } from 'owa-localize';
import { getStore as getListViewStore, isPriorityView } from 'owa-mail-list-store';
import { getMailboxInfo } from 'owa-mail-mailboxinfo';
import { observer } from 'owa-mobx-react';
import React from 'react';
import {
    dismissNudgeTooltip,
    prioritizeNudgeTextTemplate,
    prioritizeNudgeTextBold,
    prioritizeNudgeTextDescription,
    prioritizeNudgeTextDescriptionOnDelivery,
} from './PrioritizeNudge.locstring.json';
import { nudgedIcon, boldText } from './PrioritizeNudge.scss';
import type { MailFolderTableQuery } from 'owa-mail-list-store';
import { logPrioritizeTelemetryEvent } from 'owa-copilot-telemetry/lib/prioritizeTelemetryLogger';
import { lazyOpenPriorityView } from 'owa-mail-priority-inbox';
import { isPriorityNudgeEnabled, isPrioritizeOnDeliveryOn } from 'owa-copilot-settings-utils';
import MailListNudge from './MailListNudge';
import type FocusedViewFilter from 'owa-service/lib/contract/FocusedViewFilter';

export interface PrioritizeNudgeProps {
    tableViewId: string;
}

const icon = <ArrowCircleUpSparkleRegular className={nudgedIcon} />;

export default observer(function PrioritizeNudge(props: PrioritizeNudgeProps): JSX.Element | null {
    const tableView = getListViewStore().tableViews.get(props.tableViewId);
    const tableQuery = tableView?.tableQuery as MailFolderTableQuery;
    const mailboxInfo = getMailboxInfo(tableView);

    const isPrioritizeViewSelected = isPriorityView(tableQuery);

    const userStore = getUserStore(mailboxInfo);
    const { prioritizeRequestStatus } = userStore;

    const isNudged = prioritizeRequestStatus === PrioritizeRequestStatus.Nudged;

    React.useEffect(() => {
        if (isNudged) {
            logPrioritizeTelemetryEvent('PrioritizeNudgeRendered', mailboxInfo);
        }
    }, [isNudged, mailboxInfo]);

    const onClick = React.useCallback(() => {
        if (tableView) {
            lazyOpenPriorityView.importAndExecute(mailboxInfo, 'Nudge' /* source */);
        }
    }, [mailboxInfo, tableView]);

    const onDismiss = React.useCallback(
        (evt: React.MouseEvent<unknown> | React.KeyboardEvent<unknown>) => {
            evt.stopPropagation();
            logPrioritizeTelemetryEvent('PrioritizeNudgeDismissed', mailboxInfo);

            updatePrioritizeRequestStatusMutator(
                mailboxInfo,
                PrioritizeRequestStatus.None,
                true /* shouldUpdatePrioritizeNudgeTimestamp */
            );
        },
        [mailboxInfo]
    );

    if (
        !isNudged ||
        isPrioritizeViewSelected ||
        !isPriorityNudgeEnabled(mailboxInfo) ||
        tableQuery?.focusedViewFilter === 1
    ) {
        return null;
    }

    const text = formatToArray(
        loc(prioritizeNudgeTextTemplate),
        <span className={boldText}>{loc(prioritizeNudgeTextBold)}</span>,
        isPrioritizeOnDeliveryOn(mailboxInfo)
            ? loc(prioritizeNudgeTextDescriptionOnDelivery)
            : loc(prioritizeNudgeTextDescription)
    );

    return (
        <MailListNudge
            icon={icon}
            text={text}
            dismissTooltip={loc(dismissNudgeTooltip)}
            onClick={onClick}
            onDismiss={onDismiss}
        />
    );
}, 'PrioritizeNudge') as React.FunctionComponent<PrioritizeNudgeProps>;
